/**
 * Checkout Error Handler
 *
 * Centralized error handling for checkout operations.
 * Routes errors to appropriate displays, logs to Sentry, and provides retry logic.
 */

import { captureError } from '@/lib/errors';
import { devError } from '@/lib/utils/devLog';
import { getErrorMeta } from './codes';
import { getErrorMessage } from './messages';
import type {
  AddressError,
  CheckoutError,
  CheckoutErrorResult,
  ErrorCategory,
  NetworkError,
  PaymentError,
  ServerError,
  StockError,
  UnknownError,
  ValidationError,
} from './types';

/**
 * Error handler options
 */
export interface ErrorHandlerOptions {
  /** Whether to log to Sentry */
  logToSentry?: boolean;
  /** Locale for error messages */
  locale?: 'en' | 'de' | 'fr';
  /** Context for error logging */
  context?: Record<string, unknown>;
  /** Source operation name */
  operation?: string;
}

/**
 * Retry configuration by error category
 */
export const RETRY_CONFIG: Record<ErrorCategory, { maxRetries: number; delayMs: number }> = {
  validation: { maxRetries: 0, delayMs: 0 },
  stock: { maxRetries: 0, delayMs: 0 },
  payment: { maxRetries: 1, delayMs: 1000 },
  address: { maxRetries: 0, delayMs: 0 },
  network: { maxRetries: 3, delayMs: 1000 },
  auth: { maxRetries: 0, delayMs: 0 },
  expired: { maxRetries: 0, delayMs: 0 },
  rate_limit: { maxRetries: 3, delayMs: 2000 },
  server: { maxRetries: 2, delayMs: 1500 },
  unknown: { maxRetries: 1, delayMs: 1000 },
};

/**
 * Create a checkout error from raw error data
 */
export function createCheckoutError(
  code: string,
  message: string,
  options: {
    field?: string;
    context?: Record<string, unknown>;
  } = {}
): CheckoutError {
  const meta = getErrorMeta(code);

  const baseError = {
    code,
    message,
    category: meta.category,
    severity: meta.severity,
    recoverable: meta.recoverable,
    retryable: meta.retryable,
    userMessage: meta.userMessageKey,
    field: options.field,
    context: options.context,
  };

  // Return typed error based on category
  switch (meta.category) {
    case 'validation':
      return {
        ...baseError,
        category: 'validation',
        field: options.field ?? 'unknown',
      } as ValidationError;

    case 'stock':
      return {
        ...baseError,
        category: 'stock',
        productId: options.context?.productId as string | undefined,
        variantId: options.context?.variantId as string | undefined,
      } as StockError;

    case 'payment':
      return {
        ...baseError,
        category: 'payment',
        paymentMethod: options.context?.paymentMethod as string | undefined,
        declineCode: options.context?.declineCode as string | undefined,
      } as PaymentError;

    case 'address':
      return {
        ...baseError,
        category: 'address',
        field: options.field ?? 'address',
        suggestedValue: options.context?.suggestedValue as string | undefined,
      } as AddressError;

    case 'network':
      return {
        ...baseError,
        category: 'network',
        statusCode: options.context?.statusCode as number | undefined,
        endpoint: options.context?.endpoint as string | undefined,
      } as NetworkError;

    case 'server':
      return {
        ...baseError,
        category: 'server',
        statusCode: options.context?.statusCode as number | undefined,
        requestId: options.context?.requestId as string | undefined,
      } as ServerError;

    default:
      return {
        ...baseError,
        category: 'unknown',
        originalError: options.context?.originalError,
      } as UnknownError;
  }
}

/**
 * Parse errors from Saleor GraphQL response
 */
export function parseSaleorErrors(
  errors: Array<{ field?: string | null; code?: string | null; message?: string | null }>
): CheckoutError[] {
  return errors.map((error) =>
    createCheckoutError(error.code ?? 'UNKNOWN', error.message ?? 'An error occurred', {
      field: error.field ?? undefined,
    })
  );
}

/**
 * Parse errors from OrderApp response
 */
export function parseOrderAppErrors(
  errors: Array<{
    field?: string | null;
    code?: string | null;
    message?: string | null;
    context?: string | null;
  }>
): CheckoutError[] {
  return errors.map((error) => {
    let parsedContext: Record<string, unknown> = {};
    if (error.context) {
      try {
        parsedContext = JSON.parse(error.context);
      } catch {
        parsedContext = { raw: error.context };
      }
    }

    return createCheckoutError(error.code ?? 'UNKNOWN', error.message ?? 'An error occurred', {
      field: error.field ?? undefined,
      context: parsedContext,
    });
  });
}

/**
 * Parse a network error
 */
export function parseNetworkError(error: unknown): CheckoutError {
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return createCheckoutError('NETWORK_ERROR', 'Network request failed', {
      context: { originalError: error },
    });
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return createCheckoutError('TIMEOUT', 'Request timed out', {
      context: { originalError: error },
    });
  }

  return createCheckoutError('UNKNOWN', String(error), {
    context: { originalError: error },
  });
}

/**
 * Log error to Sentry (when available)
 * Falls back to console logging in development
 */
function logToSentry(error: CheckoutError, options: ErrorHandlerOptions): void {
  // Skip info-level errors
  if (error.severity === 'info') return;

  // Only log critical/error severity in production
  if (process.env.NODE_ENV === 'production' && error.severity === 'warning') {
    return;
  }

  const errorDetails = {
    code: error.code,
    message: error.message,
    category: error.category,
    severity: error.severity,
    operation: options.operation,
    context: { ...error.context, ...options.context },
  };

  const sentryError = new Error(`[Checkout] ${error.code}: ${error.message}`);
  sentryError.name = `CheckoutError.${error.category}`;

  captureError(sentryError, {
    extra: {
      ...errorDetails,
      recoverable: error.recoverable,
      retryable: error.retryable,
      field: 'field' in error ? error.field : undefined,
    },
    tags: {
      'checkout.error_code': error.code,
      'checkout.category': error.category,
      'checkout.severity': error.severity,
      ...(options.operation && { 'checkout.operation': options.operation }),
    },
  });

  devError('[Checkout Error]', errorDetails);
}

/**
 * Handle checkout errors
 * Routes to appropriate display, logs to Sentry if configured
 */
export function handleCheckoutError(
  error: CheckoutError,
  options: ErrorHandlerOptions = {}
): CheckoutError {
  const { logToSentry: shouldLog = true, locale = 'en' } = options;

  // Enrich error with user-friendly message
  const enrichedError = {
    ...error,
    userMessage: error.userMessage
      ? getErrorMessage(error.userMessage, locale)
      : getErrorMessage('errors.unknown', locale),
  };

  // Log to Sentry if enabled
  if (shouldLog) {
    logToSentry(enrichedError, options);
  }

  return enrichedError;
}

/**
 * Handle multiple checkout errors
 */
export function handleCheckoutErrors(
  errors: CheckoutError[],
  options: ErrorHandlerOptions = {}
): CheckoutError[] {
  return errors.map((error) => handleCheckoutError(error, options));
}

/**
 * Create an error result
 */
export function errorResult(errors: CheckoutError[]): CheckoutErrorResult {
  return { success: false, errors };
}

/**
 * Get field errors from error list
 */
export function getFieldErrors(errors: CheckoutError[]): Record<string, string> {
  const fieldErrors: Record<string, string> = {};

  for (const error of errors) {
    if ('field' in error && error.field && error.userMessage) {
      fieldErrors[error.field] = error.userMessage;
    }
  }

  return fieldErrors;
}

/**
 * Get the first error message (for toast/alert display)
 */
export function getFirstErrorMessage(
  errors: CheckoutError[],
  locale: 'en' | 'de' | 'fr' = 'en'
): string {
  if (errors.length === 0) {
    return getErrorMessage('errors.unknown', locale);
  }

  const error = errors[0];
  return error.userMessage ?? getErrorMessage('errors.unknown', locale);
}

/**
 * Check if any errors are retryable
 */
export function hasRetryableErrors(errors: CheckoutError[]): boolean {
  return errors.some((error) => error.retryable);
}

/**
 * Get retry configuration for errors
 */
export function getRetryConfig(errors: CheckoutError[]): {
  shouldRetry: boolean;
  delayMs: number;
  maxRetries: number;
} {
  if (!hasRetryableErrors(errors)) {
    return { shouldRetry: false, delayMs: 0, maxRetries: 0 };
  }

  // Use the config from the most severe retryable error
  const retryableError = errors.find((e) => e.retryable);
  if (!retryableError) {
    return { shouldRetry: false, delayMs: 0, maxRetries: 0 };
  }

  const config = RETRY_CONFIG[retryableError.category];
  return {
    shouldRetry: config.maxRetries > 0,
    delayMs: config.delayMs,
    maxRetries: config.maxRetries,
  };
}

/**
 * Sleep helper for retry delays
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Retry an operation with exponential backoff
 *
 * NOTE: This is a checkout-specific retry utility with onRetry callback
 * for error logging/monitoring. For simple GraphQL query retries without
 * callbacks, the simpler `withRetry` in `lib/graphql/client.ts` is used.
 */
export async function withRetry<T>(
  operation: () => Promise<T>,
  options: {
    maxRetries?: number;
    delayMs?: number;
    onRetry?: (attempt: number, error: unknown) => void;
  } = {}
): Promise<T> {
  const { maxRetries = 3, delayMs = 1000, onRetry } = options;

  let lastError: unknown;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      lastError = error;

      if (attempt < maxRetries) {
        const delay = delayMs * Math.pow(2, attempt); // Exponential backoff
        onRetry?.(attempt + 1, error);
        await sleep(delay);
      }
    }
  }

  throw lastError;
}
